import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { RefereeCertification } from '@/models/tournament';
import type { RefereeWorkloadRow } from './useRefereeWorkload';

interface WorkloadTableProps {
  data: RefereeWorkloadRow[];
}

type SortKey = 'name' | 'certification' | 'rating' | 'matchCount' | 'workPoints';
type SortDir = 'asc' | 'desc';

function certLabel(cert: RefereeCertification, t: (key: string) => string): string {
  switch (cert) {
    case RefereeCertification.FIFAInternational: return t('FIFA International');
    case RefereeCertification.AFCElite: return t('AFC Elite');
    case RefereeCertification.NationalLevelA: return t('National Level A');
    default: return t('Unknown');
  }
}

function loadIndicator(matchesPerMonth: number, t: (key: string) => string): { label: string; className: string } {
  if (matchesPerMonth > 8) {
    return { label: t('High'), className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' };
  }
  if (matchesPerMonth >= 5) {
    return { label: t('Medium'), className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' };
  }
  return { label: t('Low'), className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' };
}

const WorkloadTable: React.FC<WorkloadTableProps> = ({ data }) => {
  const { t } = useTranslation('tournament');
  const [sortKey, setSortKey] = useState<SortKey>('matchCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const sorted = useMemo(() => {
    const copy = [...data];
    copy.sort((a, b) => {
      let cmp = 0;
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        cmp = aVal.localeCompare(bVal);
      } else if (typeof aVal === 'number' && typeof bVal === 'number') {
        cmp = aVal - bVal;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return copy;
  }, [data, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={14} className="text-gray-400" aria-hidden="true" />;
    return sortDir === 'asc'
      ? <ArrowUp size={14} className="text-primary" aria-hidden="true" />
      : <ArrowDown size={14} className="text-primary" aria-hidden="true" />;
  };

  const columns: { key: SortKey; label: string }[] = [
    { key: 'name', label: t('Official Name') },
    { key: 'certification', label: t('Certification') },
    { key: 'rating', label: t('Rating') },
    { key: 'matchCount', label: t('Matches') },
    { key: 'workPoints', label: t('Work Points') },
  ];

  if (data.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-12 text-center" data-testid="workload-empty">
        <p className="text-muted-foreground">{t('No officials found')}</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="workload-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              {/* Sorting lives on a real <button> inside the <th>, not on the <th> itself: a
                  `<th onClick>` is mouse-only — no tab stop, no Enter/Space — and screen readers
                  were never told the column was sortable or which way it currently runs.
                  `aria-sort` on the <th> is what carries that state. */}
              {columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="px-4 py-3 text-start font-medium text-gray-600 dark:text-gray-400 select-none"
                  aria-sort={sortKey === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                >
                  <button
                    type="button"
                    onClick={() => handleSort(col.key)}
                    className="inline-flex items-center gap-1.5 -mx-1 px-1 py-0.5 rounded font-medium hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 transition-colors"
                    data-testid={`sort-${col.key}`}
                  >
                    {col.label}
                    <SortIcon col={col.key} />
                  </button>
                </th>
              ))}
              <th scope="col" className="px-4 py-3 text-start font-medium text-gray-600 dark:text-gray-400">
                {t('Load')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => {
              const load = loadIndicator(row.matchCount, t);
              return (
                // No row click. It used to be `cursor-pointer` + navigate to
                // '/tournament-management/officials' with NO official in the URL, so every row led to
                // the same unfiltered list — a drill-down affordance that drilled nowhere. The
                // official list reads its search from local state, not the query string, so there is
                // no honest target to pass; the affordance is removed rather than faked.
                <tr
                  key={row.userId}
                  className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                  data-testid={`workload-row-${row.userId}`}
                >
                  <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                    {row.name}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {certLabel(row.certification, t)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {row.rating.toFixed(1)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300 font-semibold">
                    {row.matchCount}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {row.workPoints.toFixed(1)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${load.className}`}>
                      {load.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WorkloadTable;
